// Editable Bill of Sale text. Defaults live here (single source of truth);
// the backend stores only the keys a dealer has changed. Dynamic values use
// {{tokens}} that are substituted from the deal at render time.

export const TOKENS = ["{{buyer_name}}", "{{vin}}", "{{odometer}}", "{{dealer_name}}", "{{date}}"];

export const DEFAULT_TEXTS = {
  // ---- Page 1 — headings ----
  doc_title: "Used Motor Vehicle Purchase Agreement",
  sec_buyer: "Buyer",
  sec_vehicle: "Vehicle Description",
  sec_price: "Price Information",
  sec_disclosures: "Disclosures",
  sec_finance: "Finance Terms",
  sec_service_plan: "Service Plan",
  sec_remarks: "Remarks",

  // ---- Page 1 — purchaser statement ----
  purchaser_ack:
    "I, the purchaser, {{buyer_name}}, agree to purchase the following vehicle from you, the dealer, on the terms set out in this agreement including the vehicle information document which forms part of this agreement.",

  // ---- Page 1 — disclosures ----
  disc_privacy_title: "Privacy Statement:",
  disc_privacy_body:
    "By signing this contract you consent to the dealer contacting you in the future and to the sharing of information with associated businesses so they may provide you with timely information about their services. You may withdraw your consent in writing at any time.",
  disc_incentive_title: "Incentive:",
  disc_incentive_body: "The dealership may receive a fee from the institution that is providing my financing.",
  disc_financing_title: "Financing Information:",
  disc_financing_body:
    "I confirm that the financial institution providing financing for the purchase of this vehicle has provided me with the initial disclosure statement regarding financial information as required under the Consumer Protection Act, 2002.",
  disc_mvda_title: "Motor Vehicle Dealers Act:",
  disc_mvda_body: "I have received the vehicle information required under the Motor Vehicle Dealers Act, 2002.",

  // ---- Page 1 — sales final & service plan ----
  sales_final_title: "Sales Final",
  sales_final_body:
    "Please review the entire contract, including all attached statements, before signing. This contract is final and binding once you have signed it unless the motor vehicle dealer has failed to comply with certain legal obligations.",
  service_plan_prompt: "This vehicle was sold with a service plan?",

  // ---- Page 2 — certificates & notices ----
  p2_safety_title: "Safety Standards Certificate",
  p2_safety_body:
    "A used motor vehicle that is sold in a deal in Ontario must have a valid Safety Standards Certificate before the vehicle can be registered and plated in the buyer's name, unless it is sold \"as-is\".",
  p2_asis_title: "Vehicle Sold \"AS-IS\"",
  p2_asis_body:
    "If a vehicle is sold \"as-is\", it is being sold without any warranty as to its mechanical condition, fitness or roadworthiness. The vehicle is not certified, may not be fit for use, and may require substantial repairs at the purchaser's expense. It may not pass a Safety Standards inspection and may not be capable of passing an emissions test. The purchaser acknowledges and accepts these conditions where applicable.",
  p2_omvic_title:
    "Important Information Respecting Motor Vehicle Sales — Ontario Motor Vehicle Industry Council (OMVIC)",
  p2_omvic_body:
    "If you have a concern with this transaction you may contact the dealer first. If the matter is not resolved you may contact OMVIC. Eligible consumers who suffer a financial loss in a transaction with a registered dealer may have recourse to the Motor Vehicle Dealers Compensation Fund. You may have additional rights at law.",
  p2_omvic_contact: "OMVIC: 1-800-943-6002 · www.omvic.on.ca",
  p2_camvap_title: "Canadian Motor Vehicle Arbitration Plan (CAMVAP)",
  p2_camvap_body:
    "CAMVAP is a program that helps resolve disputes about alleged manufacturing defects or implementation of the manufacturer's new vehicle warranty, where applicable.",

  // ---- Page 3 — used-vehicle disclosures ----
  p3_title: "Disclosures for a Used Vehicle",
  p3_vin_line: "VIN: {{vin}}",
  p3_odometer_title: "1. Odometer",
  p3_odometer_body:
    "Total distance travelled: {{odometer}}. ☐ The odometer reading is believed to be accurate. ☐ The odometer reading is not the actual distance the vehicle has travelled. ☐ Odometer reading is unknown / replaced / faulty.",
  p3_prioruse_title: "2. Prior Use",
  p3_prioruse_body: "☐ Daily rental ☐ Police/emergency ☐ Taxi/limousine ☐ None of the above to the seller's knowledge.",
  p3_priordamage_title: "3. Prior Damage / Condition",
  p3_priordamage_body:
    "☐ Total loss / branded (irreparable, salvage, rebuilt) ☐ Structural/frame damage ☐ Fire or flood damage ☐ Prior repairs exceeding $3,000 ☐ Recovered theft ☐ Manufacturer buyback / warranty cancelled ☐ Registered in another jurisdiction. To the best of the Seller's knowledge, information and belief, none of the statements in this Part 3 apply to the vehicle unless indicated above.",

  // ---- Page 3 — per-item checkbox labels (TICKET-DEAL-001) ----
  // The three combined *_body keys above are KEPT so any deal signed before this
  // ticket renders exactly as it was signed; they are hidden from Settings. New
  // and unsigned deals use the individual item labels below instead.
  p3_odometer_reading: "Total distance travelled: {{odometer}}",
  p3_odo_accurate: "The odometer reading is believed to be accurate.",
  p3_odo_not_actual: "The odometer reading is not the actual distance the vehicle has travelled.",
  p3_odo_unknown: "Odometer reading is unknown / replaced / faulty.",
  p3_use_rental: "Daily rental",
  p3_use_police: "Police / emergency",
  p3_use_taxi: "Taxi / limousine",
  p3_use_none: "None of the above to the seller's knowledge",
  p3_dmg_total_loss: "Total loss / branded (irreparable, salvage, rebuilt)",
  p3_dmg_structural: "Structural / frame damage",
  p3_dmg_fire_flood: "Fire or flood damage",
  p3_dmg_repairs: "Prior repairs exceeding $3,000",
  p3_dmg_theft: "Recovered theft",
  p3_dmg_buyback: "Manufacturer buyback / warranty cancelled",
  p3_dmg_jurisdiction: "Registered in another jurisdiction",
  p3_dmg_footer:
    "To the best of the Seller's knowledge, information and belief, none of the statements in this Part 3 apply to the vehicle unless indicated above.",
  p3_details_label: "Additional details",

  // ---- Page 4 — terms & conditions ----
  p4_title: "Terms and Conditions",
  term_1_title: "1. Privacy.",
  term_1_body: "The dealer collects, uses and discloses personal information to complete and service this transaction and as permitted by law.",
  term_2_title: "2. The Vehicle.",
  term_2_body: "\"Vehicle\" includes all accessories, equipment and parts attached at delivery.",
  term_3_title: "3. Distance Travelled.",
  term_3_body: "The dealer does not warrant the accuracy of the odometer beyond the disclosures provided.",
  term_4_title: "4. Title.",
  term_4_body: "Title remains with the dealer until the purchase price is paid in full.",
  term_5_title: "5. Transfer Documents.",
  term_5_body: "The buyer authorizes the dealer to complete applications required to transfer registration.",
  term_6_title: "6. Failure of Payment.",
  term_6_body: "If any payment is dishonoured the buyer is in breach and the dealer may repossess the vehicle and recover its costs.",
  term_7_title: "7. Cancellation.",
  term_7_body: "If delivery is delayed more than 120 days the buyer may cancel and recover deposits, subject to law.",
  term_8_title: "8. Trade-In.",
  term_8_body: "Misrepresentation of a trade-in vehicle, or a material change in its condition prior to delivery, permits the dealer to re-appraise the trade-in.",
  term_9_title: "9. Financing & Payment.",
  term_9_body: "The buyer acknowledges financing conditions, credit checks and financing statements where applicable.",
  term_10_title: "10. NSF Charges.",
  term_10_body: "NSF/returned payments are subject to administration fees and interest.",
  term_11_title: "11. Warranties.",
  term_11_body: "Except as required by law or expressly stated, the vehicle is sold without dealer warranty; manufacturer warranties (if any) apply.",
  term_12_title: "12. Changes in Tax.",
  term_12_body: "Any change in taxes payable will adjust the purchase price accordingly.",
  term_13_title: "13. Governing Law.",
  term_13_body: "This agreement is governed by the laws of Ontario and Canada and the parties have requested it be drawn up in English.",
  term_14_title: "14. Error / Assignment.",
  term_14_body: "Clerical errors may be corrected; the buyer may not assign this agreement without consent, the dealer may.",
};

// TICKET-DEAL-001 — page-3 disclosure checkbox groups. Each item has a stable
// `id` (stored per deal in `bos_checks`) and a `key` (its editable label in
// DEFAULT_TEXTS). `mode` drives the tick behaviour.
export const P3_CHECK_GROUPS = [
  {
    group: "odometer",
    titleKey: "p3_odometer_title",
    introKey: "p3_odometer_reading",
    mode: "one", // one-of-three, clearable
    items: [
      { id: "odo_accurate", key: "p3_odo_accurate" },
      { id: "odo_not_actual", key: "p3_odo_not_actual" },
      { id: "odo_unknown", key: "p3_odo_unknown" },
    ],
  },
  {
    group: "prioruse",
    titleKey: "p3_prioruse_title",
    mode: "multi_excl", // multi-select; the exclusive item clears the others
    items: [
      { id: "use_rental", key: "p3_use_rental" },
      { id: "use_police", key: "p3_use_police" },
      { id: "use_taxi", key: "p3_use_taxi" },
      { id: "use_none", key: "p3_use_none", exclusive: true },
    ],
  },
  {
    group: "priordamage",
    titleKey: "p3_priordamage_title",
    footerKey: "p3_dmg_footer",
    mode: "multi", // independent
    items: [
      { id: "dmg_total_loss", key: "p3_dmg_total_loss" },
      { id: "dmg_structural", key: "p3_dmg_structural" },
      { id: "dmg_fire_flood", key: "p3_dmg_fire_flood" },
      { id: "dmg_repairs", key: "p3_dmg_repairs" },
      { id: "dmg_theft", key: "p3_dmg_theft" },
      { id: "dmg_buyback", key: "p3_dmg_buyback" },
      { id: "dmg_jurisdiction", key: "p3_dmg_jurisdiction" },
    ],
  },
];

// Returns a new `bos_checks` map after toggling one item, honouring the group's
// mode (odometer one-of-three; prior-use "None" exclusive; damage independent).
export function toggleCheck(groupKey, checks, itemId) {
  const group = P3_CHECK_GROUPS.find((g) => g.group === groupKey);
  const next = { ...(checks || {}) };
  const on = !!next[itemId];
  if (!group) { if (on) delete next[itemId]; else next[itemId] = true; return next; }
  if (group.mode === "one") {
    group.items.forEach((it) => delete next[it.id]);
    if (!on) next[itemId] = true;
  } else if (group.mode === "multi_excl") {
    const item = group.items.find((it) => it.id === itemId);
    if (item?.exclusive) {
      group.items.forEach((it) => delete next[it.id]);
      if (!on) next[itemId] = true;
    } else {
      if (on) delete next[itemId];
      else { next[itemId] = true; group.items.filter((it) => it.exclusive).forEach((it) => delete next[it.id]); }
    }
  } else {
    if (on) delete next[itemId]; else next[itemId] = true;
  }
  return next;
}

// Fixed-size note character limits — MEASURED (Playwright) against the DEFAULT
// signature-spot positions so a full box never reaches a spot:
//  • Page-1 Remarks box: 750×77px, holds ~544 representative chars; its bottom
//    sits 49.6px above the default page-1 buyer signature. Limit 500 (< 544).
//  • Page-3 details box: 750×96px, holds ~681 chars; its bottom sits 250px above
//    the default page-3 buyer initials. Limit 600 (< 681).
export const REMARKS_MAX = 500;
export const DETAILS_MAX = 600;

// Editor layout: ordered groups of fields. `rows` drives textarea height.
const term = (n) => [
  { key: `term_${n}_title`, label: `Clause ${n} — heading`, rows: 1 },
  { key: `term_${n}_body`, label: `Clause ${n} — text`, rows: 3 },
];

export const TEXT_GROUPS = [
  {
    group: "Page 1 — headings",
    fields: [
      { key: "doc_title", label: "Document title", rows: 1 },
      { key: "sec_buyer", label: "Section: Buyer", rows: 1 },
      { key: "sec_vehicle", label: "Section: Vehicle Description", rows: 1 },
      { key: "sec_price", label: "Section: Price Information", rows: 1 },
      { key: "sec_disclosures", label: "Section: Disclosures", rows: 1 },
      { key: "sec_finance", label: "Section: Finance Terms", rows: 1 },
      { key: "sec_service_plan", label: "Section: Service Plan", rows: 1 },
      { key: "sec_remarks", label: "Section: Remarks", rows: 1 },
    ],
  },
  {
    group: "Page 1 — purchaser statement",
    fields: [{ key: "purchaser_ack", label: "Purchaser acknowledgement", rows: 3, tokens: true }],
  },
  {
    group: "Page 1 — disclosures",
    fields: [
      { key: "disc_privacy_title", label: "Privacy — heading", rows: 1 },
      { key: "disc_privacy_body", label: "Privacy — text", rows: 4 },
      { key: "disc_incentive_title", label: "Incentive — heading", rows: 1 },
      { key: "disc_incentive_body", label: "Incentive — text", rows: 2 },
      { key: "disc_financing_title", label: "Financing Information — heading", rows: 1 },
      { key: "disc_financing_body", label: "Financing Information — text", rows: 4 },
      { key: "disc_mvda_title", label: "Motor Vehicle Dealers Act — heading", rows: 1 },
      { key: "disc_mvda_body", label: "Motor Vehicle Dealers Act — text", rows: 2 },
    ],
  },
  {
    group: "Page 1 — sales final & service plan",
    fields: [
      { key: "sales_final_title", label: "Sales Final — heading", rows: 1 },
      { key: "sales_final_body", label: "Sales Final — text", rows: 3 },
      { key: "service_plan_prompt", label: "Service plan prompt", rows: 1 },
    ],
  },
  {
    group: "Page 2 — certificates & notices",
    fields: [
      { key: "p2_safety_title", label: "Safety Standards — heading", rows: 1 },
      { key: "p2_safety_body", label: "Safety Standards — text", rows: 3 },
      { key: "p2_asis_title", label: "AS-IS — heading", rows: 1 },
      { key: "p2_asis_body", label: "AS-IS — text", rows: 5 },
      { key: "p2_omvic_title", label: "OMVIC — heading", rows: 2 },
      { key: "p2_omvic_body", label: "OMVIC — text", rows: 4 },
      { key: "p2_omvic_contact", label: "OMVIC — contact line", rows: 1 },
      { key: "p2_camvap_title", label: "CAMVAP — heading", rows: 1 },
      { key: "p2_camvap_body", label: "CAMVAP — text", rows: 3 },
    ],
  },
  {
    group: "Page 3 — used-vehicle disclosures",
    fields: [
      { key: "p3_title", label: "Heading", rows: 1 },
      { key: "p3_vin_line", label: "VIN line", rows: 1, tokens: true },
      { key: "p3_odometer_title", label: "Odometer — heading", rows: 1 },
      { key: "p3_odometer_reading", label: "Odometer — reading line", rows: 1, tokens: true },
      { key: "p3_odo_accurate", label: "Odometer — option 1", rows: 2 },
      { key: "p3_odo_not_actual", label: "Odometer — option 2", rows: 2 },
      { key: "p3_odo_unknown", label: "Odometer — option 3", rows: 2 },
      { key: "p3_prioruse_title", label: "Prior Use — heading", rows: 1 },
      { key: "p3_use_rental", label: "Prior Use — option 1", rows: 1 },
      { key: "p3_use_police", label: "Prior Use — option 2", rows: 1 },
      { key: "p3_use_taxi", label: "Prior Use — option 3", rows: 1 },
      { key: "p3_use_none", label: "Prior Use — option 4 (exclusive)", rows: 1 },
      { key: "p3_priordamage_title", label: "Prior Damage — heading", rows: 1 },
      { key: "p3_dmg_total_loss", label: "Prior Damage — option 1", rows: 2 },
      { key: "p3_dmg_structural", label: "Prior Damage — option 2", rows: 1 },
      { key: "p3_dmg_fire_flood", label: "Prior Damage — option 3", rows: 1 },
      { key: "p3_dmg_repairs", label: "Prior Damage — option 4", rows: 1 },
      { key: "p3_dmg_theft", label: "Prior Damage — option 5", rows: 1 },
      { key: "p3_dmg_buyback", label: "Prior Damage — option 6", rows: 1 },
      { key: "p3_dmg_jurisdiction", label: "Prior Damage — option 7", rows: 1 },
      { key: "p3_dmg_footer", label: "Prior Damage — closing statement", rows: 3 },
      { key: "p3_details_label", label: "Additional-details box — label", rows: 1 },
    ],
  },
  {
    group: "Page 4 — terms & conditions",
    fields: [
      { key: "p4_title", label: "Heading", rows: 1 },
      ...Array.from({ length: 14 }, (_, i) => term(i + 1)).flat(),
    ],
  },
];

export function mergeTexts(overrides) {
  return { ...DEFAULT_TEXTS, ...(overrides || {}) };
}

// Only the keys a dealer has actually changed — keeps storage lean and lets
// future default edits flow through untouched keys.
export function textOverrides(texts) {
  const out = {};
  for (const k of Object.keys(DEFAULT_TEXTS)) {
    if (texts[k] != null && texts[k] !== DEFAULT_TEXTS[k]) out[k] = texts[k];
  }
  return out;
}

function tokenMap(d = {}, dealer = {}) {
  const km = d.vehicle_odometer ? `${Number(d.vehicle_odometer).toLocaleString()} km` : "__________";
  return {
    "{{buyer_name}}": d.customer_name || "____________________",
    "{{vin}}": d.vehicle_vin || "____________________",
    "{{odometer}}": km,
    "{{dealer_name}}": dealer.name || "",
    "{{date}}": d.deal_date || (d.created_at || "").slice(0, 10) || "",
  };
}

export function applyTokens(str, d, dealer) {
  if (!str) return str;
  const map = tokenMap(d, dealer);
  return str.replace(/\{\{\w+\}\}/g, (m) => (m in map ? map[m] : m));
}

// Returns a getter `(key) => resolvedText` for the document.
export function makeText(texts, d, dealer) {
  const merged = mergeTexts(texts);
  return (key) => applyTokens(merged[key] ?? "", d, dealer);
}
